import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { LANGS, useI18n } from "../i18n";
import { initials } from "../lib/fleet";
import { navigate, type Route } from "../router/hash";
import { useSession } from "../state/session";
import { Icon } from "./Icon";
import { useMediaQuery } from "./useMediaQuery";

export type Crumb = { label: string; href?: string };

/**
 * Touch tablets (ADR 0030 annex, phase C2): 44 px controls left the breadcrumb ~126 px at 1024 px. In this range the
 * bar is compact (search folds into the quick switcher, short demo badge, priority truncation; responsive.css).
 */
export const TOPBAR_COMPACT_QUERY = "(min-width: 720px) and (max-width: 1279px) and (pointer: coarse)";

/**
 * A crumb's text: when it is cut off with an ellipsis (priority truncation on touch tablets), the full text is
 * the tooltip. Re-checked whenever the crumb's box changes size.
 */
function CrumbText({ label, href, current }: { label: string; href?: string; current: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setCut(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", check);
      return () => window.removeEventListener("resize", check);
    }
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label]);
  const title = cut ? label : undefined;
  return href && !current ? (
    <a ref={ref as RefObject<HTMLAnchorElement>} href={href} title={title}>
      {label}
    </a>
  ) : (
    <span ref={ref as RefObject<HTMLSpanElement>} aria-current={current ? "page" : undefined} title={title}>
      {label}
    </span>
  );
}

export function TopBar({
  crumbs,
  route,
  alarmCount,
  onOpenMenu,
  menuOpen,
  onOpenQuick,
}: {
  crumbs: Crumb[];
  route: Route;
  alarmCount: number;
  onOpenMenu: () => void;
  menuOpen: boolean;
  /** Opens the Ctrl+K quick switcher. */
  onOpenQuick?: () => void;
}) {
  const { t, lang, setLang } = useI18n();
  const { user, provider } = useSession();
  const routeQuery = route.name === "vehicles" ? (route.query.get("q") ?? "") : "";
  const [q, setQ] = useState(routeQuery);

  useEffect(() => setQ(routeQuery), [routeQuery]);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const site = route.name === "vehicles" ? (route.query.get("site") ?? undefined) : undefined;
    const type = route.name === "vehicles" ? (route.query.get("type") ?? undefined) : undefined;
    navigate("/vehicles", { q: q.trim() || undefined, site, type });
  };

  const compact = useMediaQuery(TOPBAR_COMPACT_QUERY);
  const alarmLabel = alarmCount > 0 ? t("topbar.alarmsCount", { count: alarmCount }) : t("topbar.noAlarms");

  return (
    <header className={compact ? "topbar topbar-compact" : "topbar"}>
      <button
        type="button"
        className="icon-button menu-button"
        onClick={onOpenMenu}
        aria-expanded={menuOpen}
        aria-controls="rail"
        title={t("nav.openMenu")}
      >
        <Icon name="menu" size={20} label={t("nav.openMenu")} />
      </button>
      <img src="/brand/expartise-logo.png" alt={t("app.logoAlt")} className="topbar-logo" width={104} height={33} />

      <nav className="breadcrumb" aria-label={t("topbar.breadcrumb")}>
        <ol>
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={i} className={last ? "is-current" : undefined}>
                <CrumbText label={c.label} href={c.href} current={last} />
                {!last && <Icon name="chevronRight" size={14} className="breadcrumb-sep" />}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="topbar-actions">
        {provider.mode === "demo" && (
          <span className="demo-chip" title={compact ? t("app.demoBadge") : undefined}>
            <span className="demo-chip-full">{t("app.demoBadge")}</span>
            <span className="demo-chip-short" aria-hidden="true">
              {t("app.demoBadgeShort")}
            </span>
          </span>
        )}
        <form role="search" className="search" onSubmit={onSearch}>
          <Icon name="search" size={16} className="search-icon" />
          <label htmlFor="global-search" className="sr-only">
            {t("topbar.searchLabel")}
          </label>
          <input
            id="global-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("topbar.searchPlaceholder")}
            autoComplete="off"
          />
        </form>
        {onOpenQuick && (
          // Phones and touch tablets see a search icon (no keyboard shortcut to show); desktops see "Ctrl K".
          <button type="button" className="quick-open" onClick={onOpenQuick} aria-keyshortcuts="Control+K Meta+K" title={t("quick.open")}>
            <Icon name="search" size={18} className="quick-open-icon" />
            <span className="sr-only">{t("quick.open")}</span>
            <kbd aria-hidden="true">Ctrl K</kbd>
          </button>
        )}
        <div className="lang-switch" role="group" aria-label={t("topbar.language")}>
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={lang === l}
              className={lang === l ? "is-on" : undefined}
              onClick={() => {
                if (l === lang) return;
                setLang(l);
                // E-mails follow the console language (profiles.locale); the UI works even if this fails.
                if (user) void provider.updateLocale(l).catch(() => undefined);
              }}
              title={l === "tr" ? t("lang.trName") : t("lang.enName")}
            >
              {l === "tr" ? t("lang.tr") : t("lang.en")}
            </button>
          ))}
        </div>
        <a href="#/alarms" className="icon-button bell" aria-label={`${t("topbar.alarms")}: ${alarmLabel}`} title={alarmLabel}>
          <Icon name="bell" size={19} />
          {alarmCount > 0 && <span className="bell-badge mono">{alarmCount > 99 ? "99+" : alarmCount}</span>}
        </a>
        {user && (
          <a href="#/profile" className="avatar" title={t("topbar.account", { name: user.displayName })} aria-label={t("topbar.account", { name: user.displayName })}>
            {initials(user.displayName)}
          </a>
        )}
      </div>
    </header>
  );
}
